import { Injectable } from '@angular/core';
import { StateValidator } from '../interfaces/state-validator';
import { ConstraintTypeEnum } from '../enum/constraint-type.enum';
import { ValidationMode } from '../enum/validation-mode.enum';
import { Violation } from '../models/violation';
import { countries } from '../list/countries';
import { ConstraintEnum } from '../enum/constraint.enum';
import { Card } from '../models/card';
import { CardTypeEnum } from '../enum/card-type.enum';

@Injectable({ providedIn: 'root' })
export class GeoValidator implements StateValidator {

    supports(constraintType: ConstraintTypeEnum): boolean {
        return constraintType === ConstraintTypeEnum.SHIPMENT;
    }

    private validateCard(card: Card, cardType: CardTypeEnum, violations: Violation[]): void
    {
        if (card.country === null || card.country === '') {
            switch (cardType) {
                case CardTypeEnum.SHIPPER:
                    violations.push({
                        id: ConstraintEnum.SHIPPER_COUNTRY_EMPTY,
                        path: 'shipment.shipper.country',
                        message: $localize`Shipper country is required`
                    });
                    break;
                case CardTypeEnum.DESTINATION:
                    violations.push({
                        id: ConstraintEnum.DESTINATION_COUNTRY_EMPTY,
                        path: 'shipment.destination.country',
                        message: $localize`Destination country is required`
                    });
            }
            return;
        }
        const countryGeo = countries.find(c => c.code === card.country);
        if (countryGeo === null) {
            switch (cardType) {
                case CardTypeEnum.SHIPPER:
                    violations.push({
                        id: ConstraintEnum.SHIPPER_COUNTRY_INVALID,
                        path: 'shipment.shipper.country',
                        message: $localize`Invalid shipper country`
                    });
                    break;
                case CardTypeEnum.DESTINATION:
                    violations.push({
                        id: ConstraintEnum.DESTINATION_COUNTRY_INVALID,
                        path: 'shipment.destination.country',
                        message: $localize`Invalid destination country`
                    });
                    break;
                case CardTypeEnum.BROKER:
                    violations.push({
                        id: ConstraintEnum.BROKER_COUNTRY_INVALID,
                        path: 'shipment.broker.country',
                        message: $localize`Invalid broker country`
                    });
                    break;
                case CardTypeEnum.SOLD_TO:
                    violations.push({
                        id: ConstraintEnum.SOLD_TO_COUNTRY_INVALID,
                        path: 'shipment.soldTo.country',
                        message: $localize`Invalid sold-to country`
                    });
            }
            return;
        }
        if (countryGeo.enablePostalCode) {
            if (countryGeo.postalCodeRegex) {
                if (!countryGeo.postalCodeRegex.test(card.postalCode)) {
                    if (countryGeo.code === 'US') {
                        switch (cardType) {
                            case CardTypeEnum.SHIPPER:
                                violations.push({
                                    id: ConstraintEnum.SHIPPER_POSTALCODE_FORMAT,
                                    path: 'shipment.shipper.postalCode',
                                    message: $localize`Shipper ZIP Code format should be "#####" or "#####-####"`
                                });
                                break;
                            case CardTypeEnum.DESTINATION:
                                violations.push({
                                    id: ConstraintEnum.DESTINATION_POSTALCODE_FORMAT,
                                    path: 'shipment.destination.postalCode',
                                    message: $localize`Destination ZIP Code format should be "#####" or "#####-####"`
                                });
                                break;
                            case CardTypeEnum.BROKER:
                                violations.push({
                                    id: ConstraintEnum.BROKER_POSTALCODE_FORMAT,
                                    path: 'shipment.broker.postalCode',
                                    message: $localize`Broker ZIP Code format should be "#####" or "#####-####"`
                                });
                                break;
                            case CardTypeEnum.SOLD_TO:
                                violations.push({
                                    id: ConstraintEnum.SHIPPER_POSTALCODE_FORMAT,
                                    path: 'shipment.soldTo.postalCode',
                                    message: $localize`Sold-To ZIP Code format should be "#####" or "#####-####"`
                                });
                                break;
                        }
                    } else {
                        switch (cardType) {
                            case CardTypeEnum.SHIPPER:
                                violations.push({
                                    id: ConstraintEnum.SHIPPER_POSTALCODE_FORMAT,
                                    path: 'shipment.shipper.postalCode',
                                    message: countryGeo.postalCodeFormats ? $localize`Shipper Postal Code format should be "${countryGeo.postalCodeFormats}"`
                                        : $localize`Invalid shipper postal code`
                                });
                                break;
                            case CardTypeEnum.DESTINATION:
                                violations.push({
                                    id: ConstraintEnum.DESTINATION_POSTALCODE_FORMAT,
                                    path: 'shipment.destination.postalCode',
                                    message: countryGeo.postalCodeFormats ? $localize`Destination Postal Code format should be "${countryGeo.postalCodeFormats}"`
                                        : $localize`Invalid destination postal code`
                                });
                                break;
                        }
                    }
                }
            }
            if (countryGeo.enableProvinces && countryGeo.provinces.length > 0) {
                const province = countryGeo.provinces.find(p => p.code === card.province);
                if (!province) {
                    switch (cardType) {
                        case CardTypeEnum.SHIPPER:
                            violations.push({
                                id: ConstraintEnum.SHIPPER_PROVINCE_INVALID,
                                path: 'shipment.shipper.province',
                                message: $localize`Invalid shipper province`
                            });
                            break;
                        case CardTypeEnum.DESTINATION:
                            violations.push({
                                id: ConstraintEnum.DESTINATION_PROVINCE_INVALID,
                                path: 'shipment.destination.province',
                                message: $localize`Invalid destination province`
                            });
                            break;
                    }
                }
            }
        }
    }

    validate(state: any, constraintType: ConstraintTypeEnum, mode: ValidationMode): Violation[] {
        if (mode === ValidationMode.NONE) {
            return [];
        }

        const shipment = state;
        const violations: Violation[] = [];

        this.validateCard(shipment.shipper, CardTypeEnum.SHIPPER, violations);
        this.validateCard(shipment.destination, CardTypeEnum.DESTINATION, violations);

        if (mode === ValidationMode.SUBMIT) {
            if (shipment.broker.country) {
                this.validateCard(shipment.broker, CardTypeEnum.BROKER, violations);
            }
            if (shipment.soldTo.country) {
                this.validateCard(shipment.soldTo, CardTypeEnum.SOLD_TO, violations);
            }
        }

        return violations;
    }

}
